import { useState, useCallback } from "react";
import fetch from "cross-fetch";
import { ChevronsUpDown, Check, AlertCircle } from "lucide-react";
import { AccessibleAccount } from "@/components/context/useCurrentAccount";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { cn } from "@/lib/utils";
import { getRoleLabel } from "@/components/context/useTeamMembers";

interface AccountSwitcherProps {
  currentAccountId: string;
  accounts: AccessibleAccount[];
}

export function AccountSwitcher({ currentAccountId, accounts }: AccountSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSwitch = useCallback(
    async (accountId: string) => {
      if (accountId === currentAccountId || loading) return;

      setLoading(true);
      setError(null);

      try {
        const response = await fetch("/web_api/accounts/switch", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ account_id: accountId }),
        });
        const data = await response.json();

        if (data.status === "ok") {
          window.location.href = "/dashboard/monitors";
        } else {
          setError(data.error || "Failed to switch account");
        }
      } catch {
        setError("Failed to switch account. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [currentAccountId, loading],
  );

  if (accounts.length <= 1) {
    return null;
  }

  const currentAccount = accounts.find((a) => a.id === currentAccountId);

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => setOpen(true)}
        className="w-full justify-between px-3 text-left font-normal"
      >
        <div className="flex flex-col items-start">
          <span className="text-sm font-medium truncate max-w-[180px]">
            {currentAccount?.team_name || currentAccount?.email || "Current Account"}
          </span>
          <span className="text-xs text-muted-foreground">{accounts.length} accounts</span>
        </div>
        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} size="md">
        <DialogHeader onClose={() => setOpen(false)}>
          <DialogTitle>Switch Account</DialogTitle>
          <DialogDescription>
            You have access to multiple accounts. Select one to switch to.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="space-y-2">
            {accounts.map((account) => {
              const isCurrent = account.id === currentAccountId;
              return (
                <button
                  key={account.id}
                  onClick={() => handleSwitch(account.id)}
                  disabled={loading || isCurrent}
                  className={cn(
                    "w-full flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer",
                    isCurrent
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50 hover:bg-accent",
                    loading && "opacity-50 cursor-not-allowed",
                  )}
                >
                  <div className="flex flex-col items-start text-left">
                    <span className="font-medium">{account.team_name || account.email}</span>
                    {account.team_name && (
                      <span className="text-sm text-muted-foreground">{account.email}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{getRoleLabel(account.role)}</Badge>
                    {isCurrent && <Check className="h-4 w-4 text-primary" />}
                  </div>
                </button>
              );
            })}
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
}
